import { FetchBaseQueryError } from '@reduxjs/toolkit/query';
import { createApi } from '@reduxjs/toolkit/query/react';
import { appPortalBaseQuery } from '@/features/baseQuery.ts';
import {
  AgGridLayoutPreferenceDto,
  AgGridLayoutScope,
  SaveAgGridLayoutRequest,
} from '@/features/user-preferences/user-preferences.def.ts';

type SaveAgGridLayoutMutationRequest = AgGridLayoutScope & SaveAgGridLayoutRequest;

const getAgGridLayoutTagId = ({ environment, gridId }: AgGridLayoutScope) =>
  `${environment}:${gridId}`;

export const userPreferencesApi = createApi({
  reducerPath: 'userPreferencesApi',
  baseQuery: appPortalBaseQuery(),
  tagTypes: ['AgGridLayout'],
  endpoints: (builder) => ({
    getAgGridLayout: builder.query<AgGridLayoutPreferenceDto | null, AgGridLayoutScope>({
      async queryFn({ environment, gridId }, _api, _extraOptions, baseQuery) {
        const result = await baseQuery({
          url: `/user-preferences/ag-grid-layouts/${encodeURIComponent(gridId)}`,
          method: 'GET',
          params: { environment },
        });

        if (result.error) {
          if ((result.error as FetchBaseQueryError).status === 404) {
            return { data: null };
          }

          return { error: result.error as FetchBaseQueryError };
        }

        return { data: result.data as AgGridLayoutPreferenceDto };
      },
      providesTags: (_result, _error, scope) => [
        { type: 'AgGridLayout', id: getAgGridLayoutTagId(scope) },
      ],
    }),
    saveAgGridLayout: builder.mutation<AgGridLayoutPreferenceDto, SaveAgGridLayoutMutationRequest>({
      query: ({ environment, gridId, schemaVersion, state }) => ({
        url: `/user-preferences/ag-grid-layouts/${encodeURIComponent(gridId)}`,
        method: 'PUT',
        body: {
          environment,
          schemaVersion,
          state,
        } satisfies SaveAgGridLayoutRequest,
      }),
      invalidatesTags: (_result, _error, scope) => [
        { type: 'AgGridLayout', id: getAgGridLayoutTagId(scope) },
      ],
    }),
    deleteAgGridLayout: builder.mutation<void, AgGridLayoutScope>({
      async queryFn({ environment, gridId }, _api, _extraOptions, baseQuery) {
        const result = await baseQuery({
          url: `/user-preferences/ag-grid-layouts/${encodeURIComponent(gridId)}`,
          method: 'DELETE',
          params: { environment },
        });

        if (result.error) {
          if ((result.error as FetchBaseQueryError).status === 404) {
            return { data: undefined };
          }

          return { error: result.error as FetchBaseQueryError };
        }

        return { data: undefined };
      },
      invalidatesTags: (_result, _error, scope) => [
        { type: 'AgGridLayout', id: getAgGridLayoutTagId(scope) },
      ],
    }),
  }),
});
